import { useState, useRef, useCallback, type MouseEventHandler } from 'react';
import { cacheFactory } from '@shared/helpers/function.helpers';
import { is } from './dom.guards';

export const OVERLAY_ROOT_CLASSES = [
  'MuiModal-root',
  'MuiPopover-root',
  'MuiDialog-root',
];
/**
 * Check if the node has an overlay parent or is an overlay itself.
 * Overlay classes are defined in {@link OVERLAY_ROOT_CLASSES}
 * @param node node to check. search is inclusive (if the node is the overlay root, it will return true)
 * @returns true if the node has or is an overlay parent
 */
export const hasOverlayParent = (() => {
  // Element.closest accepts a CSS selector, so we need to convert the class list to a CSS selector, like ".class1, .class2, ..."
  const overlayCssSelector = OVERLAY_ROOT_CLASSES.map((name) =>
    name.startsWith('.') ? name : `.${name}`,
  ).join(', ');
  return (node: Element): boolean => node.closest(overlayCssSelector) !== null;
})();

/**
 * Returns true when keyboard focus is on the document root only (no focused
 * interactive control). Used so custom Tab handlers can avoid calling
 * {@link KeyboardEvent.preventDefault} while focus is on buttons, links,
 * menus, or other tab stops.
 *
 * @param active - Typically {@link Document.activeElement}; pass `null` when absent.
 */
export function isDocumentRootFocus(active: Element | null): boolean {
  if (!active) return true;
  return active === document.body || active === document.documentElement;
}

const hasContentEditableAttribute = (element: HTMLElement): boolean => {
  const value = element.getAttribute('contenteditable');
  return value !== null && value !== 'false';
};

/**
 * True when `active` is a text-entry surface (input, textarea, or any
 * `contenteditable` host) so global keyboard shortcuts can bail out instead
 * of stealing keystrokes from the user's typing.
 */
export const isTextInputElement = (element: Element | null): boolean => {
  if (!element) return false;
  if (is.Input(element) || is.TextArea(element)) return true;
  return (
    is.HTMLElement(element) &&
    (element.isContentEditable || hasContentEditableAttribute(element))
  );
};

/**
 * True when the user has a non-empty DOM text highlight so Cmd/Ctrl+C should
 * use the native clipboard instead of an app-level copy action (e.g. copying
 * an import-row climate mapping).
 *
 * @param selection - Typically {@link window.getSelection}; injectable for tests.
 */
export const hasNonEmptyTextSelection = (
  selection: Pick<Selection, 'toString'> | null | undefined = typeof window !==
  'undefined'
    ? window.getSelection()
    : null,
): boolean => Boolean(selection?.toString());

/**
 * Blur an event target when it is actually focusable, so `Enter`-to-commit
 * handlers can drop focus without asserting the target's element type.
 */
export const blurEventTarget = (target: EventTarget | null): void => {
  if (target instanceof HTMLElement) target.blur();
};

const INTERACTIVE_FORM_CONTROL_SELECTOR = [
  'input:not([type="hidden"]):not([disabled])',
  'textarea:not([disabled])',
  'select:not([disabled])',
  'button:not([disabled])',
  '[contenteditable=""]',
  '[contenteditable="true"]',
  '[role="combobox"]:not([aria-disabled="true"])',
  '[role="textbox"]:not([aria-disabled="true"])',
  '[role="searchbox"]:not([aria-disabled="true"])',
  '[role="spinbutton"]:not([aria-disabled="true"])',
].join(', ');

/**
 * True when the element is (or is inside) an interactive form control so global
 * keyboard shortcuts should not steal keys from the user.
 */
export const isInteractiveFormControlElement = (
  element: Element | null,
): boolean => {
  if (!element || !is.HTMLElement(element)) return false;
  if (element.isContentEditable || hasContentEditableAttribute(element)) {
    return true;
  }
  if (element.matches(INTERACTIVE_FORM_CONTROL_SELECTOR)) return true;
  return element.closest(INTERACTIVE_FORM_CONTROL_SELECTOR) !== null;
};

/**
 * Open MUI modal, dialog, and popover roots (closed ones stay `aria-hidden`).
 */
export const OPEN_OVERLAY_SELECTOR = OVERLAY_ROOT_CLASSES.map((className) => {
  const root = className.startsWith('.') ? className : `.${className}`;
  return `${root}:not([aria-hidden="true"])`;
}).join(', ');

/**
 * True when a MUI modal, dialog, or popover is open in the document (even if
 * focus has not moved into it yet).
 */
export const hasOpenOverlayInDocument = (): boolean =>
  document.querySelector(OPEN_OVERLAY_SELECTOR) !== null;

/**
 * Open MUI overlay roots currently in the document.
 */
export const getOpenOverlayElements = (): Element[] =>
  Array.from(document.querySelectorAll(OPEN_OVERLAY_SELECTOR));

/**
 * React mouse handler that calls `stopPropagation` on the event so ancestor
 * `onClick` targets (e.g. background deselect) do not run.
 */
export const stopClickPropagation: MouseEventHandler = (event) => {
  event.stopPropagation();
};

let canvas: HTMLCanvasElement;

/**
 * Uses canvas.measureText to compute and return the width of the given text of given font in pixels.
 *
 * @param {String} text The text to be rendered.
 * @param {String} font The css font descriptor that text is to be rendered with (e.g. "bold 14px verdana").
 *
 * @see https://stackoverflow.com/questions/118241/calculate-text-width-with-javascript/21015393#21015393
 */
function getTextWidth(text: string, font: string) {
  return cacheFactory(
    () => {
      // re-use canvas object for better performance
      if (!canvas) {
        canvas = document.createElement('canvas');
      }
      const context = canvas.getContext('2d');
      if (!context) {
        return 0;
      }
      context.font = font;
      const { width, actualBoundingBoxRight, actualBoundingBoxLeft } =
        context.measureText(text);

      return Math.max(
        width,
        actualBoundingBoxRight - actualBoundingBoxLeft, // Italic characters might overflow the width
      );
    },
    `getTextWidth["${font}"]["${text}"]`,
    [text, font],
  );
}

function getCanvasFontStyle(el = document.body): string {
  const style = window.getComputedStyle(el, null);
  const fontWeight = style.getPropertyValue('font-weight') || 'normal';
  const fontStyle = style.getPropertyValue('font-style') || 'normal';
  const fontSize = style.getPropertyValue('font-size') || '16px';
  const fontFamily = style.getPropertyValue('font-family') || 'Times New Roman';

  return `${fontStyle} ${fontWeight} ${fontSize} ${fontFamily}`;
}
/**
 *
 * @param element Element to get the text width of.
 * @param text Text to be rendered. If not provided, the value or placeholder of the element will be used.
 * @returns
 */
export const getInputTextWidth = (
  element: HTMLInputElement,
  text?: string,
): number => {
  const renderedText = text || element.value || element.placeholder;

  const font = getCanvasFontStyle(element);
  return getTextWidth(renderedText, font);
};

export const replaceTextAndKeepTextSelection = (
  input: HTMLInputElement | HTMLTextAreaElement,
  replaceWith: string,
  replace: string = input.value,
) => {
  const currentText = input.value;

  // If the value is the same or the replace string is not found, do nothing
  if (replace === replaceWith || !currentText.includes(replace)) {
    return;
  }

  const start = input.selectionStart;
  const end = input.selectionEnd;

  input.value = currentText.replaceAll(replace, replaceWith);
  if (start === null || end === null) {
    return;
  }

  const textBefore = currentText.substring(0, end);
  const replacementCount = textBefore.split(replace).length - 1;
  const lengthDiff = (replaceWith.length - replace.length) * replacementCount;
  input.selectionStart = start;
  input.selectionEnd = end + lengthDiff;
};

/**
 * Hook to track hover state of an element with proper mount/unmount handling.
 * Returns both the hover state and a ref callback to attach to elements.
 *
 * @returns [isHovered, refCallback] - hover state and ref callback for the element
 *
 * @example
 * ```tsx
 * const [isHovered, hoverRef] = useHover<HTMLButtonElement>();
 *
 * return (
 *   <button ref={hoverRef}>
 *     {isHovered ? 'Hovered!' : 'Not hovered'}
 *   </button>
 * );
 * ```
 */
export const useHoverWithRefCallback = <T extends Element>(): [
  boolean,
  (element: T | null) => void,
] => {
  const [isHovered, setIsHovered] = useState(false);
  const cleanupRef = useRef<(() => void) | null>(null);

  const refCallback = useCallback((element: T | null) => {
    // Clean up previous element
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }

    // Reset hover state when element unmounts
    if (!element) {
      setIsHovered(false);
      return;
    }

    // Set up event listeners for new element
    const handleMouseEnter = () => setIsHovered(true);
    const handleMouseLeave = () => setIsHovered(false);

    element.addEventListener('mouseenter', handleMouseEnter);
    element.addEventListener('mouseleave', handleMouseLeave);

    cleanupRef.current = () => {
      element.removeEventListener('mouseenter', handleMouseEnter);
      element.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return [isHovered, refCallback];
};

const scrollableOverflowValues = new Set(['scroll', 'auto']);
/**
 * Check if element or any of its parents are scrollable
 * @param element Start element to check
 * @param axis Axis to check. Default is 'y' (vertical)
 * @returns true if the element or any of its parents are scrollable
 */
export const isScrollable = (
  element: HTMLElement,
  axis: 'x' | 'y' | 'xy' = 'y',
): boolean => {
  const style = window.getComputedStyle(element);

  // Check vertical scrolling
  const canScrollY =
    element.scrollHeight > element.clientHeight &&
    scrollableOverflowValues.has(style.overflowY);

  // Check horizontal scrolling
  const canScrollX =
    element.scrollWidth > element.clientWidth &&
    scrollableOverflowValues.has(style.overflowX);

  if (axis.includes('x') && canScrollX) return true;
  if (axis.includes('y') && canScrollY) return true;
  if (element.parentElement) return isScrollable(element.parentElement, axis);
  return false;
};

export const MENU_SELECTED_ITEM_SELECTOR = '[data-menu-selected="true"]';

const SCROLL_SELECTED_MENU_ITEM_INTO_CENTER: ScrollIntoViewOptions = {
  behavior: 'instant',
  block: 'center',
};

/**
 * Centers the selected menu row inside {@link root} on the first visible frame.
 * No-op when {@link root} is missing, does not overflow, or has no
 * `data-menu-selected="true"` row — so a short menu (or a paper that has not
 * become a scroll box yet) does not scroll the window.
 */
export const scrollSelectedMenuItemIntoCenter = (
  root: HTMLElement | null | undefined,
): void => {
  if (!root) return;
  if (root.scrollHeight <= root.clientHeight) return;
  const selected = root.querySelector<HTMLElement>(MENU_SELECTED_ITEM_SELECTOR);
  if (!selected) return;
  selected.scrollIntoView(SCROLL_SELECTED_MENU_ITEM_INTO_CENTER);
};
/** Whether a visible modal dialog currently owns file drops. */
export const hasOpenDialog = (): boolean => {
  const dialogs = document.querySelectorAll<HTMLElement>(
    '[role="dialog"][aria-modal="true"]',
  );
  return Array.from(dialogs).some(
    (dialog) =>
      dialog.offsetParent !== null || dialog.getClientRects().length > 0,
  );
};
